/**
 * Choosing, per pen, between exact sample times and server-side bucketing.
 *
 * The aggregate endpoint reports `toStartOfInterval` bucket starts, not sample
 * times, so every timestamp it returns is a lie of up to one bucket -- at the
 * 7-day default that is a whole minute, which is why the page never shows
 * seconds. The raw endpoint returns the real instants, but a dense pen over a
 * long window is unshippable (measured: 5 pens at ~44 samples/min over 3 days =
 * 979k points / 130MB / 24s).
 *
 * So the choice has to be made per pen, and it cannot be made from the window
 * length: sample rates vary by 200x *within one equipment family* on the same
 * site (measured at longridge over 3 days: 01-TE-* = 44.5/min, 01-VT-* =
 * 1.1/min, BOP1_01-TE-* = 0.2/min). A window-size heuristic would be guessing.
 * `countSamplesPerEntry` on the aggregate endpoint answers it exactly for one
 * cheap round trip (measured: ~0.45s, 836 bytes for 3 pens), so we ask.
 */

import { RAW_FETCH_BUDGET } from 'features/timeSeries/config/constants'

export interface RawFetchPlan {
  /** Pens to fetch from the raw endpoint, at their true sample times. */
  rawEntryIds: string[]
  /** Pens to fetch from the aggregate endpoint, bucketed as before. */
  aggregateEntryIds: string[]
}

export interface RawFetchPlanInput {
  /** Every pen the chart wants, in the caller's order. */
  entryIds: string[]
  /** Exact sample count per pen for this window, from the count probe. */
  sampleCounts: Record<string, number>
  /**
   * Buckets per pen the aggregate would ship for this window. This is the
   * yardstick: raw and aggregate rows cost the same on the wire (measured 137.3
   * vs 133.8 bytes/point), so a pen is cheaper raw exactly when it has fewer
   * points than the aggregate would have sent for it anyway.
   */
  aggregateBucketCount: number
}

/**
 * Decide which pens can be fetched raw.
 *
 * Two independent limits, for two different costs:
 *
 * 1. Per pen, against `aggregateBucketCount * MAX_POINTS_VS_BUCKETS`. Because
 *    the two endpoints cost the same per point, this is very nearly "raw iff
 *    raw is not more expensive than what we already pay". The tolerance buys
 *    exact sample times (and unaveraged values) for a bounded wire premium; past
 *    it, the server's reduction is doing real work and we keep it. The default
 *    of 1.25 is what puts the reported complaint in scope: a 1/min pen over the
 *    7-day default is 11,701 samples against 10,080 buckets -- 16% more wire to
 *    stop lying about the timestamps.
 *
 * 2. Across all pens, against `MAX_POINT_PEN_PRODUCT / pens`. Raw timestamps do
 *    not align between pens, so every raw point tends to create its own chart
 *    row (see `mergeTimeSeriesResponse`), and `generateUPlotData` rescans every
 *    row once per pen. This is a main-thread budget, not a wire budget -- raw
 *    and aggregate cost the same per byte, and a raw plan ships *fewer* bytes.
 *
 * When the shared budget binds we spend it on the *sparsest* pens first. They
 * gain the most (a pen with 662 real samples is currently padded out to 9,969
 * mostly-null buckets) and cost the least, so this serves the most pens per
 * point spent.
 *
 * A pen with no entry in `sampleCounts` is left on the aggregate path: the probe
 * did not answer for it, and guessing in the raw direction is the one mistake
 * that is expensive rather than merely disappointing.
 */
export const planRawFetch = ({
  entryIds,
  sampleCounts,
  aggregateBucketCount,
}: RawFetchPlanInput): RawFetchPlan => {
  const perPenLimit = Math.floor(
    aggregateBucketCount * RAW_FETCH_BUDGET.MAX_POINTS_VS_BUCKETS
  )

  const eligible = entryIds
    .filter((entryId) => {
      const count = sampleCounts[entryId]
      return Number.isFinite(count) && count >= 0 && count <= perPenLimit
    })
    // Ascending by count, tie-broken by id so a given chart always makes the
    // same choice and cannot flip pens between renders.
    .sort((left, right) => {
      const delta = sampleCounts[left] - sampleCounts[right]
      return delta !== 0 ? delta : left.localeCompare(right)
    })

  const rawEntryIds = new Set<string>()
  // Every pen on the chart rescans every row, so the allowance has to shrink as
  // pens are added even though the extra pens may all be bucketed.
  let budget = Math.floor(
    RAW_FETCH_BUDGET.MAX_POINT_PEN_PRODUCT / Math.max(1, entryIds.length)
  )

  for (const entryId of eligible) {
    const count = sampleCounts[entryId]
    if (count > budget) break
    budget -= count
    rawEntryIds.add(entryId)
  }

  return {
    // Preserve the caller's pen order rather than the count order we sorted by.
    rawEntryIds: entryIds.filter((entryId) => rawEntryIds.has(entryId)),
    aggregateEntryIds: entryIds.filter((entryId) => !rawEntryIds.has(entryId)),
  }
}

/**
 * Whether the count probe can possibly change the outcome for this window.
 *
 * `planRawFetch` admits a pen only when its sample count is within
 * MAX_POINTS_VS_BUCKETS of the aggregate's bucket count, so the sparsest
 * cadence that could still qualify is `windowSeconds / (buckets x multiple)`.
 * Once that gap exceeds SKIP_PROBE_ABOVE_GAP_SECONDS, no pen this page charts
 * can pass, and the probe's serialized round trip buys nothing: callers should
 * go straight to the bucketed path, exactly as if the probe had run and
 * admitted nobody. See the constant for how the cutoff was chosen.
 *
 * This is deliberately NOT the per-pen raw decision the header warns against
 * making from window length -- that stays with the probe. It only asks whether
 * the ceiling the probe would be judged against is already out of reach.
 */
export const shouldProbeForRawFetch = (
  windowSeconds: number,
  aggregateBucketCount: number
): boolean => {
  if (!Number.isFinite(windowSeconds) || windowSeconds <= 0) return false
  if (!Number.isFinite(aggregateBucketCount) || aggregateBucketCount <= 0) {
    return false
  }

  const sparsestQualifyingGapSeconds =
    windowSeconds /
    (aggregateBucketCount * RAW_FETCH_BUDGET.MAX_POINTS_VS_BUCKETS)

  return (
    sparsestQualifyingGapSeconds <=
    RAW_FETCH_BUDGET.SKIP_PROBE_ABOVE_GAP_SECONDS
  )
}

const SECONDS_PER_UNIT: Record<string, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  weeks: 604800,
  // Only ever used to size a bucket count, so a nominal month is fine.
  months: 2592000,
}

/** Seconds in one `interval` of `intervalUnit`, or null for an unknown unit. */
export const intervalSeconds = (
  interval: number,
  intervalUnit: string
): number | null => {
  const unit = SECONDS_PER_UNIT[intervalUnit]
  if (unit === undefined || !Number.isFinite(interval) || interval <= 0) {
    return null
  }
  return interval * unit
}

/**
 * How many buckets the aggregate endpoint would return per pen for this window.
 *
 * Returns null when it cannot be computed, which callers must treat as "do not
 * fetch raw": the yardstick is missing, so there is nothing to justify raw
 * against.
 */
export const aggregateBucketCountFor = (
  startDatetime: string,
  endDatetime: string,
  interval: number,
  intervalUnit: string
): number | null => {
  const windowSeconds =
    (Date.parse(endDatetime) - Date.parse(startDatetime)) / 1000
  const bucketSeconds = intervalSeconds(interval, intervalUnit)

  if (!Number.isFinite(windowSeconds) || windowSeconds <= 0) return null
  if (bucketSeconds === null) return null

  return Math.ceil(windowSeconds / bucketSeconds)
}
